import { useState, useEffect, useCallback, useRef } from "react";
import ModalDetalheNegociacao from "./ModalDetalheNegociacao";
import api from "../api";
import { t } from "../vocab";
import { etapas as etapasDaEmpresa, carregarFunil, guardarFunil, primeiraEtapa } from "../funil";
import { useTheme } from "../components/Theme";
import { FileText, Clock, CheckCircle2, Play, DollarSign, Paperclip } from "lucide-react";
import AnexosBox from "../components/AnexosBox";

const ORANGE = "#EE6A1A";

const CORES_GRUPO = [
  "#EE6A1A","#e2445c","#00c875","#fdab3d","#a25ddc","#037f4c",
  "#579bfc","#ff642e","#9cd326","#66ccff","#cab641","#784bd1",
];

// etapas do funil: cada empresa define as suas em Configuracoes
let ETAPAS = etapasDaEmpresa();
const PRIMEIRA = () => (ETAPAS[0] && ETAPAS[0].id) || "prospeccao";

const ORIGENS = ["Instagram","Google","Indicação","WhatsApp","Facebook","Site","Outro"];
const SOLUCOES = ["CFTV","Controle de acesso","Infraestrutura de TI","Cabeamento estruturado","Wi-Fi corporativo","Redes","Firewall / Segurança","Servidores","Help Desk","Service Desk","Outsourcing de TI","Monitoramento","Projeto de TI","Manutenção","Outros"];
const SEGMENTOS = ["Indústria","Construção civil","Condomínio","Logística","Transportadora","Varejo","Saúde","Educação","Hotelaria","Serviços","Agronegócio","Escritórios corporativos","Outros"];

function fmt(v) {
  if (!v && v !== 0) return "";
  return `R$${Number(v).toLocaleString("pt-BR",{minimumFractionDigits:0,maximumFractionDigits:2})}`;
}

function AvatarInicial({ nome }) {
  const ini = (nome||"?").trim().split(/\s+/).slice(0,2).map(p=>p[0]||"").join("").toUpperCase() || "?";
  const cores = ["#EE6A1A","#56BD80","#378ADD","#D4537E","#7F77DD","#EBA13A","#1D9E75","#D85A30"];
  let h = 0; for (let i=0;i<(nome||"").length;i++) h = (nome.charCodeAt(i)+((h<<5)-h));
  const cor = cores[Math.abs(h)%cores.length];
  return (
    <div style={{ width:26, height:26, borderRadius:"50%", background:cor+"22", color:cor, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0, letterSpacing:"-.02em" }}>
      {ini}
    </div>
  );
}

function fmtData(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("pt-BR",{day:"2-digit",month:"short"});
}

// Avatar

function DataInline({ value, onChange, alerta }) {
  const [edit, setEdit] = useState(false);
  const val = value ? new Date(value).toISOString().slice(0,10) : "";
  if (edit) return (
    <input type="date" defaultValue={val} autoFocus
      onBlur={e=>{ setEdit(false); if(e.target.value!==val) onChange(e.target.value); }}
      onChange={e=>{ if(e.target.value!==val){ onChange(e.target.value); setEdit(false); } }}
      style={{ fontSize:12, border:"1.5px solid #EE6A1A", borderRadius:4, padding:"1px 3px", outline:"none", fontFamily:"'Inter',sans-serif" }}/>
  );
  return (
    <span onClick={()=>setEdit(true)} style={{ fontSize:12, color: value?(alerta?"#EC5B54":"var(--text-secondary)"):"var(--text-muted)", cursor:"pointer", padding:"2px 4px", borderRadius:4 }}
      onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {value ? fmtData(value) : "+ data"}
    </span>
  );
}

function Avatar({ nome, size=16 }) {
  const cores = ["#EE6A1A","#e2445c","#00c875","#fdab3d","#a25ddc","#037f4c","#ff642e"];
  const cor   = cores[(nome?.charCodeAt(0)||0) % cores.length];
  const ini   = nome?.split(" ").slice(0,2).map(n=>n[0]).join("").toUpperCase()||"?";
  return (
    <div title={nome} style={{ width:size, height:size, borderRadius:"50%", background:cor+"22", border:`2px solid ${cor}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*.4, fontWeight:700, color:cor, flexShrink:0, fontFamily:"'Inter',sans-serif" }}>
      {ini}
    </div>
  );
}

const CORES_RESP = [["#EE6A1A18","#C25510"],["#579bfc22","#2264AD"],["#1D9E7522","#0A7A4A"],["#a25ddc22","#6B36A0"],["#EBA13A22","#8A5A12"]];
function tagsResp(principal, participantes){ return [principal, ...(participantes||[])].filter(Boolean); }
function AvatarGroup({ principal, participantes=[], size=16 }) {
  const { theme } = useTheme();
  const todos = [principal,...participantes].filter(Boolean);
  if (!todos.length) return (
    <div style={{ width:size, height:size, borderRadius:"50%", border:`1.5px dashed ${theme.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:theme.textMuted, cursor:"pointer" }}>+</div>
  );
  return (
    <div style={{ display:"flex" }}>
      {todos.slice(0,3).map((n,i)=>(
        <div key={i} style={{ marginLeft:i>0?-6:0, zIndex:10-i }}>
          <Avatar nome={n} size={size}/>
        </div>
      ))}
      {todos.length>3&&(
        <div style={{ width:size, height:size, borderRadius:"50%", background:"#f0f0f0", border:"2px solid var(--card-bg)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"#666", marginLeft:-6 }}>
          +{todos.length-3}
        </div>
      )}
    </div>
  );
}

// Badge de etapa clicável
function EtapaBadge({ stage, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef();
  const et = ETAPAS.find(e=>e.id===stage)||ETAPAS[0];

  useEffect(()=>{
    const h = e=>{ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[]);

  return (
    <div ref={ref} style={{ position:"relative", display:"inline-flex" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ background:et.color+"22", borderRadius:20, padding:"3px 11px", fontSize:11, fontWeight:600, color:et.color, cursor:"pointer", whiteSpace:"nowrap", letterSpacing:".005em", border:`1px solid ${et.color}33`, opacity:saving?.7:1 }}>
        {saving?"...":et.label}
      </div>
      {open&&(
        <div style={{ position:"absolute", top:"110%", left:0, zIndex:900, background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:8, padding:6, minWidth:180, boxShadow:"0 8px 24px rgba(0,0,0,.12)" }}>
          {ETAPAS.map(e=>(
            <div key={e.id} onClick={async()=>{ setOpen(false); setSaving(true); await onChange(e.id,e.label); setSaving(false); }}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", borderRadius:6, cursor:"pointer", background:stage===e.id?"var(--hover-bg)":"transparent" }}
              onMouseEnter={ev=>ev.currentTarget.style.background="var(--hover-bg)"}
              onMouseLeave={ev=>ev.currentTarget.style.background=stage===e.id?"var(--hover-bg)":"transparent"}>
              <div style={{ width:10, height:10, borderRadius:2, background:e.color, flexShrink:0 }}/>
              <span style={{ fontSize:13, color:"var(--text-primary)", fontWeight:stage===e.id?600:400 }}>{e.label}</span>
              {stage===e.id&&<span style={{ marginLeft:"auto", color:e.color, fontSize:12 }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline edit
function IE({ value, onChange, placeholder="", type="text", style:s={}, align="left" }) {
  const [edit, setEdit]   = useState(false);
  const [val,  setVal]    = useState(value||"");
  const ref = useRef();
  useEffect(()=>setVal(value||""),[value]);
  useEffect(()=>{ if(edit&&ref.current) ref.current.focus(); },[edit]);
  const commit=()=>{ setEdit(false); if(String(val)!==String(value||"")) onChange(val); };
  if(edit) return (
    <input ref={ref} type={type} value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{ if(e.key==="Enter")commit(); if(e.key==="Escape"){setVal(value||"");setEdit(false);} }}
      style={{ background:"transparent", border:"none", borderBottom:"1.5px solid #EE6A1A", outline:"none", color:"var(--text-primary)", fontSize:13, width:"100%", padding:"1px 0", fontFamily:"'Inter',sans-serif", textAlign:align, ...s }}/>
  );
  return (
    <div onClick={()=>setEdit(true)} title={value||placeholder} style={{ cursor:"text", fontSize:12, lineHeight:1.1, color:value?"var(--text-primary)":"var(--text-muted)", fontFamily:"'Inter',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", padding:"0px 4px", borderRadius:4, textAlign:align, ...s }}
      onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {value||placeholder}
    </div>
  );
}

// Modal responsáveis
function ModalResp({ lead, usuarios, onClose, onSave }) {
  const [principal, setPrincipal]       = useState(lead.nomeResponsavel||"");
  const [participantes, setParticipantes] = useState(lead.participantes||[]);
  const [load, setLoad] = useState(false);

  const toggleP=(nome)=>{
    if(nome===principal) return;
    setParticipantes(prev=>prev.includes(nome)?prev.filter(p=>p!==nome):[...prev,nome]);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"var(--card-bg)", borderRadius:12, padding:24, width:400, boxShadow:"0 16px 48px rgba(0,0,0,.18)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:"var(--text-primary)" }}>Responsáveis</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-secondary)", fontSize:18 }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:10 }}>Responsável principal</div>
          {usuarios.map(u=>(
            <div key={u._id} onClick={()=>setPrincipal(u.nome)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer", background:principal===u.nome?"#dff0ff":"transparent", marginBottom:4 }}
              onMouseEnter={e=>{ if(principal!==u.nome) e.currentTarget.style.background="var(--hover-bg)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background=principal===u.nome?"#dff0ff":"transparent"; }}>
              <Avatar nome={u.nome} size={28}/>
              <span style={{ fontSize:14, color:"var(--text-primary)", fontWeight:principal===u.nome?600:400 }}>{u.nome}</span>
              {principal===u.nome&&<span style={{ marginLeft:"auto", color:"#EE6A1A", fontWeight:700, fontSize:13 }}>✓</span>}
            </div>
          ))}
        </div>

        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:10 }}>Participantes</div>
          {usuarios.filter(u=>u.nome!==principal).map(u=>{
            const isP=participantes.includes(u.nome);
            return (
              <div key={u._id} onClick={()=>toggleP(u.nome)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer", background:isP?"#e6f7ef":"transparent", marginBottom:4 }}
                onMouseEnter={e=>{ if(!isP) e.currentTarget.style.background="var(--hover-bg)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.background=isP?"#e6f7ef":"transparent"; }}>
                <Avatar nome={u.nome} size={28}/>
                <span style={{ fontSize:14, color:"var(--text-primary)", fontWeight:isP?600:400 }}>{u.nome}</span>
                {isP&&<span style={{ marginLeft:"auto", color:"#00c875", fontWeight:700, fontSize:13 }}>✓</span>}
              </div>
            );
          })}
        </div>

        <button onClick={async()=>{ setLoad(true); await onSave({nomeResponsavel:principal,participantes}); onClose(); setLoad(false); }} disabled={load}
          style={{ width:"100%", background:"#EE6A1A", border:"none", borderRadius:8, padding:"11px 0", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer" }}>
          {load?"Salvando...":"Confirmar"}
        </button>
      </div>
    </div>
  );
}

// Modal de oportunidade
function ModalOport({ lead, grupos, usuarios, contatos = [], onClose, onSave, onDelete, irParaOrcamento }) {
  const isNew = !lead?._id;
  const [form, setForm] = useState({
    nome:lead?.nome||"", empresa:lead?.empresa||"", telefone:lead?.telefone||"",
    email:lead?.email||"", valor:lead?.valor||"", probabilidade:lead?.probabilidade||"",
    dataFechamento:lead?.dataFechamento?new Date(lead.dataFechamento).toISOString().slice(0,10):"",
    obs:lead?.obs||"", stage:lead?.stage||primeiraEtapa(), etapaNegociacao:lead?.etapaNegociacao||"",
    grupoNegociacaoId:lead?.grupoNegociacaoId||"", grupoNegociacaoLabel:lead?.grupoNegociacaoLabel||"",
    nomeResponsavel:lead?.nomeResponsavel||"", participantes:lead?.participantes||[],
    origem:lead?.origem||"Instagram", prioridade:lead?.prioridade||"Média", servico:lead?.servico||"", nomeCliente:lead?.nomeCliente||"",
    telefone2:lead?.telefone2||"", nomeFantasia:lead?.nomeFantasia||"", numFuncionarios:lead?.numFuncionarios||"", numUnidades:lead?.numUnidades||"", segmento:lead?.segmento||"", solucoes:lead?.solucoes||[],
    proximoFollowUp:lead?.proximoFollowUp?new Date(lead.proximoFollowUp).toISOString().slice(0,10):"", followUpNota:lead?.followUpNota||"",
  });
  const [load, setLoad] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const [buscaContato, setBuscaContato] = useState("");
  const [listaAberta, setListaAberta] = useState(false);

  const inp={ width:"100%", boxSizing:"border-box", background:"var(--hover-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, padding:"9px 12px", color:"var(--text-primary)", fontSize:13, outline:"none", fontFamily:"'Inter',sans-serif" };
  const lbl={ fontSize:11, fontWeight:600, color:"var(--text-secondary)", display:"block", marginBottom:5, letterSpacing:".04em", textTransform:"uppercase" };
  const fx=e=>{ e.target.style.borderColor="#EE6A1A"; e.target.style.boxShadow="0 0 0 3px #EE6A1A18"; };
  const fb=e=>{ e.target.style.borderColor="var(--border-color)"; e.target.style.boxShadow="none"; };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:"var(--card-bg)", borderRadius:16, padding:28, width:"100%", maxWidth:540, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,.2)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:"var(--text-primary)" }}>{isNew?t("Nova oportunidade"):t("Editar oportunidade")}</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-secondary)", fontSize:20 }}>×</button>
        </div>

        {/* ---- Bloco 1: Contato ---- */}
        <div style={{ fontSize:11, fontWeight:700, color:"#EE6A1A", letterSpacing:".05em", textTransform:"uppercase", margin:"2px 0 10px" }}>Contato</div>

        <div style={{ marginBottom:12 }}>
          <label style={lbl}>Cliente *</label>
          <input value={form.nomeCliente||form.nome||""} onChange={e=>{ set("nomeCliente",e.target.value); set("nome",e.target.value); }} style={inp} placeholder="Nome do cliente ou da oportunidade" onFocus={fx} onBlur={fb}/>
        </div>

        <div style={{ position:"relative", marginBottom:12 }}>
          <label style={{ ...lbl, color:"#EE6A1A" }}>🔗 Vincular contato (cliente ou lead)</label>
          <input value={buscaContato} placeholder="Digite para buscar cliente ou lead..." style={inp}
            onChange={e=>{ setBuscaContato(e.target.value); setListaAberta(true); }}
            onFocus={()=>setListaAberta(true)}
            onBlur={()=>setTimeout(()=>setListaAberta(false),180)}/>
          {listaAberta && buscaContato.trim() && (
            <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"var(--card-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, marginTop:2, maxHeight:240, overflowY:"auto", zIndex:50, boxShadow:"0 8px 24px rgba(0,0,0,.12)" }}>
              {contatos.filter(c=>c.nome.toLowerCase().includes(buscaContato.toLowerCase())).slice(0,30).map((c,i)=>(
                <div key={i} onMouseDown={()=>{ set("nomeCliente",c.nome); set("nome",c.nome); set("telefone",c.telefone||""); set("email",c.email||""); setBuscaContato(c.nome); setListaAberta(false); }}
                  style={{ padding:"9px 12px", cursor:"pointer", fontSize:13, color:"var(--text-primary)", borderBottom:"1px solid var(--border-light)", display:"flex", justifyContent:"space-between", alignItems:"center" }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"} onMouseLeave={e=>e.currentTarget.style.background="var(--card-bg)"}>
                  <span style={{ fontWeight:600 }}>{c.nome}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:c.tipo==="Cliente"?"#10b981":"#EE6A1A", background:(c.tipo==="Cliente"?"#10b981":"#EE6A1A")+"18", padding:"2px 8px", borderRadius:99 }}>{c.tipo}</span>
                </div>
              ))}
              {contatos.filter(c=>c.nome.toLowerCase().includes(buscaContato.toLowerCase())).length===0 && (
                <div style={{ padding:"10px 12px", fontSize:12, color:"#9698a6" }}>Nenhum contato encontrado.</div>
              )}
            </div>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:8 }}>
          <div><label style={lbl}>Empresa</label><input value={form.empresa||""} onChange={e=>set("empresa",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Nome fantasia</label><input value={form.nomeFantasia||""} onChange={e=>set("nomeFantasia",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Telefone / WhatsApp</label><input value={form.telefone||""} onChange={e=>set("telefone",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Telefone 2</label><input value={form.telefone2||""} onChange={e=>set("telefone2",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>E-mail</label><input value={form.email||""} onChange={e=>set("email",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Origem do lead</label>
            <select value={form.origem||"Instagram"} onChange={e=>set("origem",e.target.value)} style={{ ...inp, appearance:"auto" }} onFocus={fx} onBlur={fb}>
              {["Indicação","Instagram","WhatsApp","Site","Facebook","Google","Telefone","Finder","Prospecção própria","Evento/Feira","Outro"].map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:8 }}>
          <div><label style={lbl}>Segmento</label>
            <select value={form.segmento||""} onChange={e=>set("segmento",e.target.value)} style={{ ...inp, appearance:"auto" }} onFocus={fx} onBlur={fb}>
              <option value="">—</option>
              {SEGMENTOS.map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Nº de funcionários</label><input value={form.numFuncionarios||""} onChange={e=>set("numFuncionarios",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Nº de unidades</label><input value={form.numUnidades||""} onChange={e=>set("numUnidades",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
        </div>
        <div style={{ marginBottom:8 }}>
          <label style={lbl}>Solução / necessidade</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:4 }}>
            {SOLUCOES.map(sol=>{ const on=(form.solucoes||[]).includes(sol); return (
              <button key={sol} type="button" onClick={()=>set("solucoes", on ? (form.solucoes||[]).filter(x=>x!==sol) : [...(form.solucoes||[]), sol])}
                style={{ padding:"5px 10px", borderRadius:99, cursor:"pointer", fontSize:11.5, fontWeight:600, border:"1.5px solid "+(on?"#EE6A1A":"var(--border-color)"), background:on?"#EE6A1A18":"var(--card-bg)", color:on?"#EE6A1A":"var(--text-secondary)" }}>{sol}</button>
            ); })}
          </div>
        </div>

        {/* ---- Bloco 2: Negócio ---- */}
        <div style={{ fontSize:11, fontWeight:700, color:"#EE6A1A", letterSpacing:".05em", textTransform:"uppercase", margin:"16px 0 10px" }}>Negócio</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:8 }}>
          <div style={{ gridColumn:"1/-1" }}><label style={lbl}>Serviço / Interesse</label><input value={form.servico||""} onChange={e=>set("servico",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Valor (R$)</label><input type="number" value={form.valor||""} onChange={e=>set("valor",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Previsão de fechamento</label><input type="date" value={form.dataFechamento||""} onChange={e=>set("dataFechamento",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/></div>
          <div><label style={lbl}>Etapa</label>
            <select value={form.stage} onChange={e=>{ const et=ETAPAS.find(x=>x.id===e.target.value); set("stage",e.target.value); set("etapaNegociacao",et?.label||""); }} style={{ ...inp, appearance:"auto" }} onFocus={fx} onBlur={fb}>
              {ETAPAS.map(e=><option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Prioridade</label>
            <div style={{ display:"flex", gap:6 }}>
              {[["Alta","#e2445c"],["Média","#EE6A1A"],["Baixa","#10b981"]].map(([pr,c])=>(
                <button key={pr} type="button" onClick={()=>set("prioridade",pr)}
                  style={{ flex:1, padding:"8px 0", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:700, border:"1.5px solid "+(form.prioridade===pr?c:"var(--border-color)"), background:form.prioridade===pr?c+"18":"var(--card-bg)", color:form.prioridade===pr?c:"var(--text-secondary)" }}>{pr}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Responsável */}
        <div style={{ marginBottom:12, marginTop:6 }}>
          <label style={{ ...lbl, marginBottom:8 }}>Responsável principal</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {usuarios.map(u=>(
              <button key={u._id} type="button" onClick={()=>set("nomeResponsavel",u.nome)}
                style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", borderRadius:6, cursor:"pointer", border:"1.5px solid "+(form.nomeResponsavel===u.nome?"#EE6A1A":"var(--border-color)"), background:form.nomeResponsavel===u.nome?"#EE6A1A18":"var(--card-bg)", color:form.nomeResponsavel===u.nome?"#EE6A1A":"var(--text-secondary)", fontSize:12, fontWeight:600 }}>
                <Avatar nome={u.nome} size={18}/>{u.nome.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Bloco 3: Observações ---- */}
        <div style={{ marginBottom:16 }}>
          <label style={lbl}>Observações</label>
          <textarea value={form.obs||""} onChange={e=>set("obs",e.target.value)} rows={2} style={{ ...inp, resize:"vertical" }}
            onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}/>
        </div>

        <div style={{ marginBottom:16, padding:"12px 14px", border:"1px solid var(--border-color)", borderRadius:10, background:"var(--hover-bg)" }}>
          <label style={{ ...lbl, color:"#EE6A1A" }}>🔔 Lembrete / tarefa futura</label>
          <div style={{ display:"grid", gridTemplateColumns:"170px 1fr", gap:10, marginTop:6 }}>
            <input type="date" value={form.proximoFollowUp||""} onChange={e=>set("proximoFollowUp",e.target.value)} style={inp} onFocus={fx} onBlur={fb}/>
            <input value={form.followUpNota||""} onChange={e=>set("followUpNota",e.target.value)} placeholder="Ex: ligar daqui 30 dias para retomar" style={inp} onFocus={fx} onBlur={fb}/>
          </div>
          <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:6 }}>Aparece no painel de follow-ups das Negociações (e no sino quando ativarmos).</div>
        </div>
        <div style={{ marginBottom:16 }}>
          <AnexosBox tipo="lead" id={lead?._id} anexos={lead?.anexos || []} />
        </div>

        {!isNew && (
          <button type="button" onClick={()=>{ irParaOrcamento && irParaOrcamento({ titulo: lead?.servico||"", servico: lead?.servico||"", valor: form.valor||lead?.valor||0, nomeCliente: lead?.nomeCliente||lead?.nome||"", responsavel: lead?.responsavel||"", nomeResponsavel: form.nomeResponsavel||lead?.nomeResponsavel||"", negociacao: lead?._id||null }); }}
            style={{ width:"100%", marginBottom:10, background:"var(--card-bg)", border:"1px solid #EE6A1A", color:"#EE6A1A", borderRadius:8, padding:"10px 0", fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            <FileText size={15}/> Gerar orcamento desta negociacao
          </button>
        )}

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={async()=>{ if(!form.nome.trim()) return alert("Nome obrigatório"); setLoad(true); try{ await onSave({...lead,...form,tipo:"negociacao"}); }finally{setLoad(false);} }} disabled={load}
            style={{ flex:1, background:"#EE6A1A", border:"none", borderRadius:8, padding:"12px 0", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", opacity:load?.7:1 }}>
            {load?"Salvando...":"Salvar"}
          </button>
          {!isNew&&<button onClick={()=>onDelete(lead._id)} style={{ background:"#feecec", border:"1px solid #f9d3d3", borderRadius:8, padding:"12px 16px", color:"#e2445c", fontWeight:600, fontSize:13, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// Modal grupo
function ModalGrupo({ grupo, onClose, onSave, onDelete }) {
  const isNew = !grupo?._id;
  const [label, setLabel] = useState(grupo?.label||"");
  const [color, setColor] = useState(grupo?.color||CORES_GRUPO[0]);
  const [load, setLoad]   = useState(false);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"var(--card-bg)", borderRadius:12, padding:24, width:360, boxShadow:"0 16px 48px rgba(0,0,0,.18)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:"var(--text-primary)" }}>{isNew?"Novo grupo":"Editar grupo"}</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-secondary)", fontSize:18 }}>×</button>
        </div>
        <input value={label} onChange={e=>setLabel(e.target.value)} placeholder="Nome do grupo..."
          style={{ width:"100%", boxSizing:"border-box", background:"var(--hover-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, padding:"10px 12px", color:"var(--text-primary)", fontSize:14, outline:"none", fontFamily:"'Inter',sans-serif", marginBottom:14 }}
          onFocus={e=>e.target.style.borderColor="#EE6A1A"} onBlur={e=>e.target.style.borderColor="var(--border-color)"}
          autoFocus onKeyDown={e=>e.key==="Enter"&&label.trim()&&(async()=>{ setLoad(true); try{await onSave({...grupo,label,color}); onClose();}finally{setLoad(false); }})()}/>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:20 }}>
          {CORES_GRUPO.map(c=>(
            <div key={c} onClick={()=>setColor(c)} style={{ width:26, height:26, borderRadius:6, background:c, cursor:"pointer", border:color===c?"3px solid var(--text-primary)":"3px solid transparent", boxSizing:"border-box" }}/>
          ))}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={async()=>{ if(!label.trim())return; setLoad(true); try{await onSave({...grupo,label,color}); onClose();}finally{setLoad(false);} }} disabled={load||!label.trim()}
            style={{ flex:1, background:color, border:"none", borderRadius:8, padding:"10px 0", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", opacity:!label.trim()?.5:1 }}>
            {load?"...":"Salvar"}
          </button>
          {!isNew&&<button onClick={()=>{ onDelete(grupo._id); onClose(); }} style={{ background:"#feecec", border:"none", borderRadius:8, padding:"10px 14px", color:"#e2445c", fontWeight:600, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// ── PIPELINE TOPO ────────────────────────────────────────
function PipelineTopo({ leads }) {
  const CARDS = ETAPAS.filter(e => e.tipo !== "perdido" && e.id !== "perdido");
  return (
    <div style={{ display:"flex", gap:10, padding:"14px 0 6px" }}>
      {CARDS.map(e => {
        const ls = leads.filter(l => (l.stage || PRIMEIRA()) === e.id);
        const v = ls.reduce((a,l)=>a+Number(l.valor||0),0);
        const cor = e.color;
        return (
          <div key={e.id} style={{ flex:1, background:cor+"14", border:`1px solid ${cor}33`, borderRadius:10, padding:"14px 16px", position:"relative", overflow:"hidden" }}>
            <div style={{ fontSize:26, fontWeight:800, color:cor, lineHeight:1, letterSpacing:"-.02em" }}>{ls.length}</div>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:6, textTransform:"uppercase", letterSpacing:".04em", fontWeight:600 }}>{e.label}</div>
            <div style={{ fontSize:13, fontWeight:700, color:v>0?cor:"var(--text-muted)", marginTop:4 }}>{v>0?fmt(v):"R$0"}</div>
          </div>
        );
      })}
    </div>
  );
}

// COMPONENTE PRINCIPAL

function KanbanNegociacoes({ leads, onEditar, onMover, podeEditar, onSalvarEtapas, onFollowUp }) {
  const [arrastando, setArrastando] = useState(null);
  const [sobre, setSobre] = useState(null);
  const [editId, setEditId] = useState(null);

  const soltar = (etapa) => {
    if (arrastando && arrastando.stage !== etapa.id) onMover(arrastando._id, etapa.id, etapa.label);
    setArrastando(null); setSobre(null);
  };

  const renomearEtapa = (etapa, txt) => {
    setEditId(null);
    const lbl = (txt || "").trim();
    if (!lbl || lbl === etapa.label) return;
    onSalvarEtapas(ETAPAS.map(e => e.id === etapa.id ? { ...e, label: lbl } : e), {});
  };
  const excluirEtapa = (etapa) => {
    if (ETAPAS.length <= 1) { alert("O funil precisa de pelo menos uma etapa."); return; }
    const nCards = leads.filter(l => (l.stage || PRIMEIRA()) === etapa.id).length;
    const idx = ETAPAS.findIndex(e => e.id === etapa.id);
    const destino = ETAPAS[idx - 1] || ETAPAS[idx + 1];
    const msg = nCards > 0
      ? 'Excluir a etapa "' + etapa.label + '"? As ' + nCards + ' negociacao(oes) vao para "' + destino.label + '".'
      : 'Excluir a etapa "' + etapa.label + '"?';
    if (!window.confirm(msg)) return;
    onSalvarEtapas(ETAPAS.filter(e => e.id !== etapa.id), nCards > 0 ? { [etapa.id]: destino.id } : {});
  };
  const adicionarEtapa = () => {
    const nome = window.prompt("Nome da nova etapa:");
    if (!nome || !nome.trim()) return;
    const nova = { id: "", label: nome.trim(), color: CORES_GRUPO[ETAPAS.length % CORES_GRUPO.length], tipo: "contato" };
    const lista = [...ETAPAS];
    const iTerm = lista.findIndex(e => e.tipo === "ganho" || e.tipo === "perdido" || e.id === "ganho" || e.id === "perdido");
    if (iTerm >= 0) lista.splice(iTerm, 0, nova); else lista.push(nova);
    onSalvarEtapas(lista, {});
  };

  return (
    <div style={{ display: "flex", gap: 9, overflowX: "auto", padding: "12px 16px 24px", alignItems: "flex-start" }}>
      {ETAPAS.map(etapa => {
        const cor = etapa.color;
        const cards = leads.filter(l => (l.stage || PRIMEIRA()) === etapa.id);
        const total = cards.reduce((a, c) => a + Number(c.valor || 0), 0);
        return (
          <div key={etapa.id}
            onDragOver={e => { e.preventDefault(); setSobre(etapa.id); }}
            onDragLeave={() => setSobre(s => s === etapa.id ? null : s)}
            onDrop={() => soltar(etapa)}
            style={{ flex: "0 0 190px", background: sobre === etapa.id ? cor + "14" : "var(--hover-bg)", borderRadius: 12, border: "1px solid " + (sobre === etapa.id ? cor : "var(--border-color)"), minHeight: 140, transition: "background .12s" }}>
            <div style={{ padding: "8px 10px", borderBottom: "2px solid " + cor, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: cor, flex: "0 0 auto" }} />
                {podeEditar && editId === etapa.id ? (
                  <input autoFocus defaultValue={etapa.label} maxLength={40}
                    onBlur={e => renomearEtapa(etapa, e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditId(null); }}
                    style={{ fontSize: 12.5, fontWeight: 800, color: "var(--text-primary)", background: "var(--card-bg)", border: "1px solid " + cor, borderRadius: 6, padding: "2px 6px", width: 120 }} />
                ) : (
                  <span onDoubleClick={() => podeEditar && setEditId(etapa.id)} title={podeEditar ? "Duplo clique para renomear" : ""}
                    style={{ fontSize: 12.5, fontWeight: 800, color: "var(--text-primary)", cursor: podeEditar ? "text" : "default", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{etapa.label}</span>
                )}
                <span style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--card-bg)", borderRadius: 10, padding: "1px 8px", flex: "0 0 auto" }}>{cards.length}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
                {total > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: "#1D9E75" }}>{fmt(total)}</span>}
                {podeEditar && editId !== etapa.id && (
                  <span onClick={() => excluirEtapa(etapa)} title="Excluir etapa"
                    style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: 13, lineHeight: 1, padding: "0 2px" }}>✕</span>
                )}
              </div>
            </div>
            <div style={{ padding: 7, display: "flex", flexDirection: "column", gap: 6 }}>
              {cards.map(c => (
                <div key={c._id}
                  draggable
                  onDragStart={() => setArrastando(c)}
                  onDragEnd={() => { setArrastando(null); setSobre(null); }}
                  onClick={() => onEditar(c)}
                  style={{ background: "var(--card-bg)", borderRadius: 9, border: "1px solid var(--border-color)", padding: "7px 9px", cursor: "grab", boxShadow: "0 1px 2px rgba(16,24,40,.05)", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 1 }}>
                    <span title={c.nomeCliente || c.nome || "-"} style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{c.nomeCliente || c.nome || "-"}</span>
                    {Number(c.valor) > 0 && <span style={{ fontSize: 12.5, fontWeight: 800, color: "#1D9E75", whiteSpace: "nowrap" }}>{fmt(c.valor)}</span>}
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--text-secondary)", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.servico || c.empresa || "\u00a0"}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                    {c.nomeResponsavel
                      ? <span title={c.nomeResponsavel} style={{ fontSize: 10, fontWeight: 700, color: "#EE6A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{String(c.nomeResponsavel).split(" ").slice(-1)[0]}</span>
                      : <span style={{ flex: 1 }} />}
                    {c.proximoFollowUp && (() => { const v = new Date(c.proximoFollowUp); const h0 = new Date(); h0.setHours(0,0,0,0); const atr = new Date(v.getFullYear(), v.getMonth(), v.getDate()) < h0; return (
                      <span title={atr ? "Follow-up vencido" : "Follow-up marcado"} style={{ fontSize: 9.5, fontWeight: 700, color: atr ? "#e2445c" : "#0a7a55", background: (atr ? "#e2445c" : "#0a7a55") + "18", borderRadius: 6, padding: "1px 5px", whiteSpace: "nowrap", flex: "0 0 auto" }}>{atr ? "venceu " : ""}{String(c.proximoFollowUp).slice(8, 10)}/{String(c.proximoFollowUp).slice(5, 7)}</span>
                    ); })()}
                    {!c.proximoFollowUp && c.dataFechamento && <span style={{ fontSize: 9.5, color: "var(--text-muted)", whiteSpace: "nowrap", flex: "0 0 auto" }}>{String(c.dataFechamento).slice(8, 10)}/{String(c.dataFechamento).slice(5, 7)}</span>}
                    <span onClick={e => { e.stopPropagation(); onFollowUp && onFollowUp(c); }} title="Marcar follow-up" style={{ cursor: "pointer", fontSize: 11, opacity: c.proximoFollowUp ? 1 : 0.3, flex: "0 0 auto" }}>🔔</span>
                  </div>
                </div>
              ))}
              {!cards.length && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", padding: "14px 0" }}>Arraste aqui</div>
              )}
            </div>
          </div>
        );
      })}
      {podeEditar && (
        <button type="button" onClick={adicionarEtapa}
          style={{ flex: "0 0 130px", minHeight: 100, borderRadius: 12, border: "2px dashed var(--border-color)", background: "transparent", color: "var(--text-muted)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", alignSelf: "stretch" }}>
          + Etapa
        </button>
      )}
    </div>
  );
}

function ModalFollowUp({ lead, onClose, onSalvo }) {
  const [data, setData] = useState(lead.proximoFollowUp ? String(lead.proximoFollowUp).slice(0,10) : "");
  const [nota, setNota] = useState(lead.followUpNota || "");
  const [salvando, setSalvando] = useState(false);
  const emDias = (d) => { const x = new Date(); x.setHours(0,0,0,0); x.setDate(x.getDate()+d); return x.toISOString().slice(0,10); };
  const salvar = async (dataISO) => {
    if (!dataISO) { alert("Escolha uma data."); return; }
    setSalvando(true);
    try {
      const titulo = "Follow-up: " + (lead.nomeCliente || lead.nome || "negociacao");
      const body = { titulo, descricao: nota || "", tipo: "tarefa", prioridade: "media", prazo: dataISO, status: "pendente", responsavelNome: lead.nomeResponsavel || "" };
      let atividadeId = lead.followUpAtividadeId;
      if (atividadeId) { await api.patch("/atividades/" + atividadeId, body).catch(async () => { const r = await api.post("/atividades", body); atividadeId = r.data._id; }); }
      else { const r = await api.post("/atividades", body); atividadeId = r.data._id; }
      await api.put("/leads/" + lead._id, { proximoFollowUp: dataISO, followUpNota: nota || "", followUpAtividadeId: atividadeId });
      onSalvo();
    } catch (e) { alert("Nao foi possivel salvar o follow-up."); setSalvando(false); }
  };
  const remover = async () => {
    setSalvando(true);
    try {
      if (lead.followUpAtividadeId) await api.delete("/atividades/" + lead.followUpAtividadeId).catch(()=>{});
      await api.put("/leads/" + lead._id, { proximoFollowUp: null, followUpNota: "", followUpAtividadeId: null });
      onSalvo();
    } catch (e) { alert("Erro ao remover."); setSalvando(false); }
  };
  const btn = { padding:"7px 12px", fontSize:12.5, fontWeight:600, borderRadius:8, border:"1px solid var(--border-color)", background:"var(--card-bg)", color:"var(--text-secondary)", cursor:"pointer" };
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(16,24,40,.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"var(--card-bg)", borderRadius:14, padding:22, width:380, maxWidth:"92vw", boxShadow:"0 20px 50px rgba(16,24,40,.25)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
          <span style={{ fontSize:15, fontWeight:800, color:"var(--text-primary)" }}>🔔 Follow-up</span>
          <span onClick={onClose} style={{ cursor:"pointer", color:"var(--text-muted)", fontSize:20, lineHeight:1 }}>×</span>
        </div>
        <div style={{ fontSize:12.5, color:"var(--text-secondary)", marginBottom:14 }}>{lead.nomeCliente || lead.nome || "Negociacao"}</div>
        <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>Lembrar em</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
          {[["3 dias",3],["7 dias",7],["15 dias",15],["30 dias",30]].map(([lb,d])=>{ const iso=emDias(d); const on=data===iso; return (
            <button key={d} type="button" onClick={()=>setData(iso)} style={{ ...btn, background:on?"#EE6A1A":"var(--card-bg)", color:on?"#fff":"var(--text-secondary)", border:on?"1px solid #EE6A1A":"1px solid var(--border-color)" }}>{lb}</button>
          ); })}
        </div>
        <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>Ou escolher a data</div>
        <input type="date" value={data} onChange={e=>setData(e.target.value)} style={{ ...btn, width:"100%", boxSizing:"border-box", cursor:"auto", marginBottom:12 }} />
        <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>Nota</div>
        <textarea value={nota} onChange={e=>setNota(e.target.value)} rows={2} placeholder="Ex: ligar pra cobrar retorno da proposta"
          style={{ width:"100%", boxSizing:"border-box", padding:"8px 10px", fontSize:12.5, borderRadius:8, border:"1px solid var(--border-color)", background:"var(--hover-bg)", color:"var(--text-primary)", resize:"vertical", marginBottom:16 }} />
        <div style={{ display:"flex", gap:8 }}>
          <button type="button" disabled={salvando} onClick={()=>salvar(data)} style={{ ...btn, flex:1, background:"#EE6A1A", color:"#fff", border:"none", opacity:salvando?0.6:1 }}>{salvando?"Salvando...":"Salvar follow-up"}</button>
          {lead.proximoFollowUp && <button type="button" disabled={salvando} onClick={remover} style={{ ...btn, color:"#e2445c" }}>Remover</button>}
        </div>
      </div>
    </div>
  );
}

export default function Negociacoes({ currentUser, irParaOrcamento }) {
  const { theme } = useTheme();
  const [leads,    setLeads]    = useState([]);
  const [grupos,   setGrupos]   = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filtroVend, setFiltroVend] = useState("");
  const [modal,    setModal]    = useState(null);
  const [view,     setView]     = useState(() => { try { return localStorage.getItem("neg_view") || "lista"; } catch(e){ return "lista"; } });
  const [modalGrupo, setModalGrupo] = useState(null);
  const [modalResp,  setModalResp]  = useState(null);
  const [novaLinha,  setNovaLinha]  = useState(null);
  const [colapsados, setColapsados] = useState({});
  const [selected,   setSelected]   = useState([]);
  const [dragId,     setDragId]     = useState(null);
  const [dragOver,   setDragOver]   = useState(null);
  const [menuId,     setMenuId]     = useState(null);
  const [detalhe,    setDetalhe]    = useState(null);
  const [followUp,   setFollowUp]   = useState(null);
  const [contatoDetalhe, setContatoDetalhe] = useState(null);
  const [menuPos,    setMenuPos]    = useState({x:0,y:0});
  const [, recarregarFunil] = useState(0);
  useEffect(() => { carregarFunil().then((l) => { ETAPAS = l; recarregarFunil((v) => v + 1); }); }, []);

  const carregar = useCallback(async()=>{
    try{
      const [lr,gr,ur,clr,ldr] = await Promise.all([
        api.get("/leads?limit=1000&tipo=negociacao"),
        api.get("/grupos"),
        api.get("/users"),
        api.get("/clientes"),
        api.get("/leads?limit=1000&tipo=lead"),
      ]);
      setLeads(lr.data.leads);
      setGrupos(gr.data.grupos);
      setUsuarios(ur.data);
      const cls = (clr.data.clientes||clr.data||[]).map(c=>({ nome:c.nome||c.empresa||"", telefone:c.telefone||"", email:c.email||"", tipo:"Cliente" }));
      const lds = (ldr.data.leads||[]).map(l=>({ nome:l.nome||l.nomeCliente||"", telefone:l.telefone||"", email:l.email||"", tipo:"Lead" }));
      setContatos([...cls, ...lds].filter(c=>c.nome));
    }catch{}finally{ setLoading(false); }
  },[]);

  useEffect(()=>{ carregar(); },[carregar]);

  const podeEditarFunil = ["admin","gerente"].includes(currentUser && currentUser.role);
  const hojeFU = new Date(); hojeFU.setHours(0, 0, 0, 0);
  const followUps = leads.filter(l => l.proximoFollowUp).map(l => { const v = new Date(l.proximoFollowUp); v.setHours(0, 0, 0, 0); return { lead: l, dias: Math.round((v - hojeFU) / 86400000) }; }).filter(x => x.dias <= 7).sort((a, b) => a.dias - b.dias);
  const salvarEtapasFunil = async (novaLista, moverMap) => {
    try {
      const { data } = await api.put("/funil", { etapasNegociacao: novaLista, moverEtapa: moverMap || {} });
      ETAPAS = (data && data.etapasNegociacao && data.etapasNegociacao.length) ? data.etapasNegociacao : ETAPAS;
      guardarFunil(data);
      recarregarFunil((v) => v + 1);
      carregar();
    } catch (err) {
      const d = err && err.response && err.response.data;
      alert((d && d.message) || "Erro ao salvar as etapas.");
    }
  };

  const atualizarEtapa = async(id,stage,etapaNegociacao)=>{
    const anterior = leads.find(l=>l._id===id);
    setLeads(prev=>prev.map(l=>l._id===id?{...l,stage,etapaNegociacao}:l));
    try {
      await api.put(`/leads/${id}`,{stage,etapaNegociacao});
    } catch (err) {
      setLeads(prev=>prev.map(l=>l._id===id?(anterior||l):l));
      alert("Não foi possível salvar a etapa: " + ((err.response&&err.response.data&&(err.response.data.erro||err.response.data.message))||err.message));
    }
  };
  const atualizarCampo = async(id,k,v)=>{
    const{data}=await api.put(`/leads/${id}`,{[k]:v});
    setLeads(prev=>prev.map(l=>l._id===id?{...l,...data}:l));
  };
  const atualizarResp = async(id,{nomeResponsavel,participantes})=>{
    const{data}=await api.put(`/leads/${id}`,{nomeResponsavel,participantes});
    setLeads(prev=>prev.map(l=>l._id===id?{...l,...data}:l));
  };
  const moverGrupo = async(leadId,grupoId,grupoLabel)=>{
    await api.put(`/leads/${leadId}`,{grupoNegociacaoId:grupoId||null,grupoNegociacaoLabel:grupoLabel||null});
    setLeads(prev=>prev.map(l=>l._id===leadId?{...l,grupoNegociacaoId:grupoId||null,grupoNegociacaoLabel:grupoLabel||null}:l));
  };
  const reordenar = async(arrastadoId, alvoId, grupoLeads)=>{
    if(arrastadoId===alvoId) return;
    const ids = grupoLeads.map(l=>l._id);
    const from = ids.indexOf(arrastadoId), to = ids.indexOf(alvoId);
    if(from<0||to<0) return;
    ids.splice(to,0,ids.splice(from,1)[0]);
    setLeads(prev=>{
      const map=Object.fromEntries(prev.map(l=>[l._id,l]));
      const reord=ids.map((id,i)=>({...map[id],ordem:i}));
      const resto=prev.filter(l=>!ids.includes(l._id));
      return [...reord,...resto];
    });
    try{ await api.put("/leads/reordenar",{ids}); }catch{}
  };
  const salvarGrupo = async(f)=>{
    if(f._id){ const{data}=await api.put(`/grupos/${f._id}`,f); setGrupos(prev=>prev.map(g=>g._id===data._id?data:g)); }
    else{ const{data}=await api.post("/grupos",f); setGrupos(prev=>[...prev,data]); }
  };
  const excluirGrupo = async(id)=>{ if (!window.confirm("Excluir este grupo? As negociações dele ficam sem grupo.")) return;
    await api.delete(`/grupos/${id}`);
    setGrupos(prev=>prev.filter(g=>g._id!==id));
    setLeads(prev=>prev.map(l=>String(l.grupoNegociacaoId)===String(id)?{...l,grupoNegociacaoId:null,grupoNegociacaoLabel:null}:l));
  };
  const salvarLead = async(f)=>{
    const limpo={ ...f,
      grupoNegociacaoId: f.grupoNegociacaoId || null,
      dataFechamento: f.dataFechamento || null,
      probabilidade: (f.probabilidade===""||f.probabilidade==null) ? null : Number(f.probabilidade),
      valor: (f.valor===""||f.valor==null) ? 0 : Number(f.valor),
      proximoFollowUp: f.proximoFollowUp || null,
    };
    if(f._id){ const{data}=await api.put(`/leads/${f._id}`,limpo); setLeads(prev=>prev.map(l=>l._id===data._id?data:l)); }
    else{ const{data}=await api.post("/leads",{...limpo,nomeCliente:(f.nomeCliente&&f.nomeCliente.trim())?f.nomeCliente:f.nome,tipo:"negociacao",stage:f.stage||"prospeccao"}); setLeads(prev=>[data,...prev]); }
    setModal(null);
  };
  const excluirLead = async(id)=>{ if (!window.confirm("Excluir esta negociação?")) return; await api.delete(`/leads/${id}`); setLeads(prev=>prev.filter(l=>l._id!==id)); setModal(null); };
  const adicionarInline = async(nome,grupoId,grupoLabel)=>{
    const{data}=await api.post("/leads",{nome:(nome && nome.trim()) || "Nova oportunidade",tipo:"negociacao",stage:"prospeccao",valor:0,grupoNegociacaoId:grupoId||null,grupoNegociacaoLabel:grupoLabel||null});
    setLeads(prev=>[data,...prev]); setNovaLinha(null);
  };

  const filtered = leads.filter(l=>{ const q=search.toLowerCase(); const okQ=!q||l.nome?.toLowerCase().includes(q)||l.empresa?.toLowerCase().includes(q); const okV=!filtroVend||tagsResp(l.nomeResponsavel,l.participantes).includes(filtroVend); return okQ&&okV; });
  const porGrupo  = (id)=>filtered.filter(l=>String(l.grupoNegociacaoId)===String(id));
  const semGrupo  = filtered.filter(l=>!l.grupoNegociacaoId);

  // Só mostrar "Sem grupo" se tiver leads sem grupo
  const todosGrupos = [
    ...grupos,
    ...(semGrupo.length>0 ? [{ _id:"__sem__", label:"Sem grupo", color:"var(--text-muted)", _virtual:true }] : [])
  ];

  // Estilos de célula
  const TH=({w,children,center})=>(
    <th style={{ padding:"9px 14px", fontSize:11, fontWeight:600, color:"var(--text-muted)", letterSpacing:".04em", textTransform:"uppercase", textAlign:center?"center":"left", borderBottom:"1px solid var(--border-light)", width:w, minWidth:w, background:"var(--card-bg)", whiteSpace:"nowrap", position:"sticky", top:52, zIndex:5, userSelect:"none" }}>{children}</th>
  );
  const TD=({w,children,center,style:s={}})=>(
    <td style={{ padding:"11px 14px", fontSize:12.5, color:"var(--text-secondary)", borderTop:"1px solid var(--border-color)", whiteSpace:"nowrap", width:w, minWidth:w, textAlign:center?"center":"left", ...s }}>{children}</td>
  );

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif", background:"var(--page-bg)", minHeight:"100vh" }} onClick={()=>setMenuId(null)}>

      {/* Header */}
      <div style={{ background:"var(--card-bg)", borderBottom:"1px solid var(--border-color)", padding:"16px 24px 0" }}>
        {/* Banner selecionados */}
        {selected.length>0&&(
          <div style={{ background:"#dff0ff", border:"1px solid #bdd9f7", borderRadius:8, padding:"8px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:600, color:"#EE6A1A" }}>{selected.length} selecionado{selected.length>1?"s":""}</span>
            <span style={{ fontSize:13, color:"var(--text-secondary)" }}>Mover para:</span>
            {grupos.map(g=>(
              <button key={g._id} onClick={async()=>{ await Promise.all(selected.map(id=>moverGrupo(id,g._id,g.label))); setSelected([]); }}
                style={{ background:g.color+"22", border:`1px solid ${g.color}44`, borderRadius:6, padding:"3px 12px", color:g.color, fontSize:12, fontWeight:600, cursor:"pointer" }}>{g.label}</button>
            ))}
            <button onClick={()=>setSelected([])} style={{ marginLeft:"auto", background:"none", border:"none", color:"var(--text-secondary)", fontSize:12, cursor:"pointer", textDecoration:"underline" }}>Cancelar</button>
          </div>
        )}

        {/* Métricas + Pipeline */}
        {followUps.length > 0 && (
          <div style={{ margin: "0 0 14px", padding: 14, borderRadius: 12, border: "1px solid #EBA13A55", background: "#EBA13A12" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#B4780F", marginBottom: 10 }}>🔔 Follow-ups de negociações</div>
            <div style={{ display: "grid", gap: 6 }}>
              {followUps.slice(0, 10).map((x, i) => { const venc = x.dias < 0; return (
                <div key={i} onClick={() => setModal(x.lead)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "var(--card-bg)", border: "1px solid var(--border-color)", cursor: "pointer" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", minWidth: 170 }}>{x.lead.nomeCliente || x.lead.nome || "—"}</span>
                  <span style={{ fontSize: 12.5, color: "var(--text-secondary)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.lead.followUpNota || x.lead.servico || ""}</span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{String(x.lead.proximoFollowUp).slice(8, 10)}/{String(x.lead.proximoFollowUp).slice(5, 7)}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: venc ? "#e2445c" : "#B4780F", background: (venc ? "#e2445c" : "#EBA13A") + "22", borderRadius: 8, padding: "2px 8px", minWidth: 96, textAlign: "center" }}>{venc ? "Venceu há " + Math.abs(x.dias) + "d" : (x.dias === 0 ? "Hoje" : "Em " + x.dias + "d")}</span>
                </div>
              ); })}
            </div>
          </div>
        )}
        {view !== "kanban" && <PipelineTopo leads={leads}/>}

        {/* Toolbar */}
        <div style={{ display:"flex", gap:10, padding:"12px 0 0", alignItems:"center" }}>
          <input placeholder="🔍 Pesquisar..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ background:"var(--hover-bg)", border:"1.5px solid var(--border-color)", borderRadius:8, padding:"7px 12px", color:"var(--text-primary)", fontSize:13, outline:"none", width:220, fontFamily:"'Inter',sans-serif" }}
            onFocus={e=>{ e.target.style.borderColor="#EE6A1A"; e.target.style.background="var(--card-bg)"; }}
            onBlur={e=>{ e.target.style.borderColor="var(--border-color)"; e.target.style.background="var(--hover-bg)"; }}/>
          <span style={{ fontSize:13, color:"var(--text-secondary)" }}>{filtered.length} oportunidade{filtered.length!==1?"s":""}</span>
          <select value={filtroVend} onChange={e=>setFiltroVend(e.target.value)} title="Filtrar por vendedor" style={{ background: filtroVend?"#EE6A1A14":"var(--hover-bg)", border:"1.5px solid "+(filtroVend?"#EE6A1A":"var(--border-color)"), borderRadius:8, padding:"7px 10px", color: filtroVend?"#EE6A1A":"var(--text-primary)", fontWeight: filtroVend?700:400, fontSize:13, outline:"none", fontFamily:"'Inter',sans-serif", appearance:"auto", cursor:"pointer" }}>
            <option value="">{t("Todos os vendedores")}</option>
            {usuarios.map(u=><option key={u._id||u.nome} value={u.nome}>{u.nome}</option>)}
          </select>
          <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
            <div style={{ display:"flex", border:"1.5px solid var(--border-color)", borderRadius:8, overflow:"hidden" }}>
              <button onClick={()=>{ setView("lista"); try{localStorage.setItem("neg_view","lista");}catch(e){} }} style={{ padding:"6px 14px", fontSize:13, fontWeight:600, border:"none", cursor:"pointer", background: view==="lista" ? "#EE6A1A" : "var(--card-bg)", color: view==="lista" ? "var(--card-bg)" : "var(--text-secondary)" }}>Lista</button>
              <button onClick={()=>{ setView("kanban"); try{localStorage.setItem("neg_view","kanban");}catch(e){} }} style={{ padding:"6px 14px", fontSize:13, fontWeight:600, border:"none", cursor:"pointer", background: view==="kanban" ? "#EE6A1A" : "var(--card-bg)", color: view==="kanban" ? "var(--card-bg)" : "var(--text-secondary)" }}>Kanban</button>
            </div>

            <button onClick={()=>setModal({})} style={{ background:"#EE6A1A", border:"none", borderRadius:8, padding:"7px 18px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", boxShadow:"0 2px 8px #EE6A1A44" }}
              onMouseEnter={e=>e.currentTarget.style.background="#0060c0"}
              onMouseLeave={e=>e.currentTarget.style.background="#EE6A1A"}>
              + Criar oportunidade
            </button>
          </div>
        </div>
      </div>

      {/* Tabela / Kanban */}
      {view === "kanban" ? (
        <KanbanNegociacoes leads={filtered} onEditar={setModal} onMover={atualizarEtapa} podeEditar={podeEditarFunil} onSalvarEtapas={salvarEtapasFunil} onFollowUp={setFollowUp} />
      ) : (
      <div style={{ padding:"16px 0 48px" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:820, height:"auto" }}>
          <thead>
            <tr>
              <TH w={36}><input type="checkbox" onChange={e=>setSelected(e.target.checked?leads.map(l=>l._id):[])}/></TH>
              <TH w={230}>Cliente</TH>
              <TH w={130} center>Etapa</TH>
              <TH w={170} center>Responsável</TH>
              <TH w={120}>Valor</TH>
              <TH w={110}>Fechamento</TH>
              <TH w={120} center>Ações</TH>
            </tr>
          </thead>

          {loading&&<tbody><tr><td colSpan={8} style={{ padding:60, textAlign:"center", color:"var(--text-secondary)" }}>Carregando...</td></tr></tbody>}

          {!loading&&[...ETAPAS,{id:"__out__",label:"Outras",color:"var(--text-muted)"}].map(etapa=>{
            const ids = ETAPAS.map(x=>x.id);
            const eLeads = etapa.id==="__out__" ? filtered.filter(l=>!ids.includes(l.stage||"prospeccao")) : filtered.filter(l=>(l.stage||"prospeccao")===etapa.id);
            if(eLeads.length===0) return null;
            const totalV = eLeads.reduce((a,l)=>a+Number(l.valor||0),0);
            return (
              <tbody key={etapa.id}>
                <tr>
                  <td colSpan={7} style={{ padding:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", borderLeft:"3px solid "+etapa.color, background:etapa.color+"0a", borderBottom:"1px solid "+etapa.color+"1a" }}>
                      <span style={{ fontWeight:700, color:etapa.color, fontSize:13 }}>{etapa.label}</span>
                      <span style={{ background:etapa.color+"1f", color:etapa.color, borderRadius:99, fontSize:11, fontWeight:700, padding:"1px 9px" }}>{eLeads.length}</span>
                      <span style={{ marginLeft:"auto", fontSize:12.5, fontWeight:800, color:etapa.color }}>{fmt(totalV)}</span>
                    </div>
                  </td>
                </tr>
                {eLeads.map(lead=>{
                  const sel = selected.includes(lead._id);
                  return (
                    <tr key={lead._id}
                      style={{ background:sel?"var(--active-bg)":"var(--card-bg)" }}
                      onMouseEnter={e=>{ if(!sel) e.currentTarget.style.background="var(--hover-bg)"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background=sel?"var(--active-bg)":"var(--card-bg)"; }}>
                      <TD w={36} center>
                        <input type="checkbox" checked={sel} onChange={()=>setSelected(prev=>prev.includes(lead._id)?prev.filter(x=>x!==lead._id):[...prev,lead._id])} onClick={e=>e.stopPropagation()}/>
                      </TD>
                      <TD w={230}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ width:8, height:8, borderRadius:"50%", background:etapa.color, flexShrink:0 }}/>
                          <IE value={lead.nomeCliente||""} onChange={v=>atualizarCampo(lead._id,"nomeCliente",v)} placeholder="—" style={{ fontWeight:600, color:"var(--text-primary)", fontSize:13.5 }}/>
                        </div>
                      </TD>
                      <TD w={130} center>
                        <EtapaBadge stage={lead.stage} onChange={(st,lb)=>atualizarEtapa(lead._id,st,lb)}/>
                      </TD>
                      <TD w={170} center>
                        <div onClick={()=>setModalResp(lead)} style={{ cursor:"pointer" }}>
                          {tagsResp(lead.nomeResponsavel, lead.participantes).length===0 ? (
                            <span style={{ color:"var(--text-muted)", fontSize:12 }}>—</span>
                          ) : (
                            <span style={{ display:"inline-flex", gap:4, flexWrap:"wrap", justifyContent:"center" }}>
                              {tagsResp(lead.nomeResponsavel, lead.participantes).map((nome,i)=>{ const c=CORES_RESP[i%CORES_RESP.length]; return <span key={i} style={{ background:c[0], color:c[1], borderRadius:99, padding:"1px 8px", fontSize:10.5, whiteSpace:"nowrap" }}>{nome}</span>; })}
                            </span>
                          )}
                        </div>
                      </TD>
                      <TD w={120}>
                        <IE value={lead.valor?fmt(lead.valor):""} onChange={v=>atualizarCampo(lead._id,"valor",Number(v.replace(/\D/g,"")))} placeholder="R$ 0" style={{ fontWeight:700, color:"#EE6A1A" }} align="left"/>
                      </TD>
                      <TD w={110}>
                        <DataInline value={lead.dataFechamento} alerta={lead.dataFechamento&&new Date(lead.dataFechamento)<new Date()&&lead.stage!=="fechado"} onChange={v=>atualizarCampo(lead._id,"dataFechamento",v)}/>
                      </TD>
                      <TD w={120} center>
                        <button onClick={(e)=>{e.stopPropagation();setModal(lead);}} style={{ background:"transparent", border:"1px solid var(--border-color)", borderRadius:6, padding:"4px 12px", fontSize:11.5, color:"var(--text-secondary)", cursor:"pointer" }}>Editar</button>
                      </TD>
                    </tr>
                  );
                })}
                <tr><td colSpan={7} style={{ height:8, background:"var(--page-bg)" }}/></tr>
              </tbody>
            );
          })}
        </table>

        <div style={{ padding:"10px 24px" }}>
          <button onClick={()=>setModal({})} style={{ background:"none", border:"1.5px dashed var(--border-color)", borderRadius:8, padding:"10px 20px", color:"var(--text-muted)", cursor:"pointer", fontSize:13, width:"100%", fontFamily:"'Inter',sans-serif" }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor="#EE6A1A"; e.currentTarget.style.color="#EE6A1A"; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor="var(--border-color)"; e.currentTarget.style.color="var(--text-muted)"; }}>
            + Adicionar oportunidade
          </button>
        </div>
      </div>
      )}

      {/* Menu ⋮ */}
      {menuId&&(
        <div style={{ position:"fixed", top:Math.min(menuPos.y, window.innerHeight-320), left:Math.min(menuPos.x, window.innerWidth-220), zIndex:3000 }} onClick={e=>e.stopPropagation()}>
          <div style={{ background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:10, padding:6, minWidth:200, boxShadow:"0 8px 24px rgba(0,0,0,.12)" }}>
            <div style={{ fontSize:11, color:"#9499a5", fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", padding:"6px 10px 4px" }}>Mover para</div>
            {grupos.map(g=>(
              <div key={g._id} onClick={()=>{ moverGrupo(menuId,g._id,g.label); setMenuId(null); }}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", borderRadius:6, cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{ width:10, height:10, borderRadius:2, background:g.color }}/><span style={{ fontSize:13, color:"var(--text-primary)" }}>{g.label}</span>
              </div>
            ))}
            <div style={{ borderTop:"1px solid var(--border-color)", margin:"4px 0" }}/>
            <div onClick={()=>{ const l=leads.find(x=>x._id===menuId); setModalResp(l); setMenuId(null); }}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", borderRadius:6, cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{ fontSize:13, color:"var(--text-primary)" }}>👥 Responsáveis</span>
            </div>
            <div onClick={()=>{ const l=leads.find(x=>x._id===menuId); setModal(l); setMenuId(null); }}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", borderRadius:6, cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--hover-bg)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{ fontSize:13, color:"var(--text-primary)" }}>✏️ Editar</span>
            </div>
          </div>
        </div>
      )}

      {detalhe&&<ModalDetalheNegociacao lead={detalhe} onClose={()=>setDetalhe(null)} onAtualizar={()=>{ carregar(); setDetalhe(prev=>prev?leads.find(l=>l._id===prev._id)||prev:null); }}/> }
      {modal!==null&&<ModalOport lead={Object.keys(modal).length?modal:null} grupos={grupos} usuarios={usuarios} contatos={contatos} onClose={()=>setModal(null)} onSave={salvarLead} onDelete={excluirLead} irParaOrcamento={irParaOrcamento}/>}
      {modalGrupo!==null&&<ModalGrupo grupo={Object.keys(modalGrupo).length?modalGrupo:null} onClose={()=>setModalGrupo(null)} onSave={salvarGrupo} onDelete={excluirGrupo}/>}
      {modalResp&&<ModalResp lead={modalResp} usuarios={usuarios} onClose={()=>setModalResp(null)} onSave={(d)=>atualizarResp(modalResp._id,d)}/>}
      {followUp&&<ModalFollowUp lead={followUp} onClose={()=>setFollowUp(null)} onSalvo={()=>{ setFollowUp(null); carregar(); }}/>}
    </div>
  );
}
